import { memo, useMemo } from "react";
import Plot from "react-plotly.js";
import type { IrrDataPoint } from "./types";
import { getIrrValue } from "./utils";

const WINDOW_DAYS = 20;

/** Overlay of active-spread IRR history for every root that is still included. */
export const IRRStackedChart = memo(function IRRStackedChart({
  irrData,
  allExcludedRoots,
  sofrAdjusted,
}: {
  irrData: IrrDataPoint[];
  allExcludedRoots: string[];
  sofrAdjusted: boolean;
}) {
  const included = useMemo(
    () => irrData.filter((d) => !allExcludedRoots.includes(d.root)),
    [irrData, allExcludedRoots]
  );

  // Window start is derived from dates only, so toggling SOFR keeps the same x range.
  // Anchored on the most recent date across all included roots so a stale root doesn't widen it.
  const windowStartMs = useMemo(() => {
    const dates = [...new Set(included.flatMap((d) => d.data.map((p) => p.dateMs)))].sort((a, b) => a - b);
    return dates.length > 0 ? dates[Math.max(0, dates.length - WINDOW_DAYS)] : null;
  }, [included]);

  const data = useMemo(
    () =>
      included.map((d) => {
        const points = d.data
          .filter((p) => windowStartMs === null || p.dateMs >= windowStartMs)
          .sort((a, b) => a.dateMs - b.dateMs);
        return {
          x: points.map((p) => p.date),
          y: points.map((p) => getIrrValue(p, sofrAdjusted)),
          connectgaps: true,
          line: { width: 2, shape: "linear" as const },
          type: "scatter" as const,
          mode: "lines" as const,
          name: d.root,
          text: points.map(() => d.displayName),
        };
      }),
    [included, windowStartMs, sofrAdjusted]
  );

  const layout = useMemo(
    () => ({
      autosize: true,
      title: `Stacked IRR History (Last ${WINDOW_DAYS} Days)`,
      dragmode: "zoom" as const,
      uirevision: `stacked-${sofrAdjusted}`,
      xaxis: { title: "Date" },
      yaxis: {
        title: { text: "IRR", standoff: 10 },
        tickformat: ".2%",
        hoverformat: ".3%",
      },
    }),
    [sofrAdjusted]
  );

  return (
    <Plot
      className="w-100 h-100"
      style={{ width: "100%", height: "100%" }}
      useResizeHandler
      data={data}
      layout={layout}
      config={{ displaylogo: false, responsive: true }}
    />
  );
});
